import React, { useState, useEffect } from 'react';
import { Send, User, X, ArrowRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const QuickTransfer = () => {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [walletType, setWalletType] = useState('cash_wallet');
  const [processing, setProcessing] = useState(false);
  const [balances, setBalances] = useState({ cash_wallet: 0, commission_wallet: 0 });

  useEffect(() => {
    fetchRecentRecipients();
    fetchBalances();
  }, []);

  const fetchRecentRecipients = async () => {
    try {
      const res = await api.get('/wallet/recent-recipients?limit=5');
      setRecipients(res.data || []);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const res = await api.get('/wallet/balances');
      setBalances(res.data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  };

  const openQuickTransfer = (recipient) => {
    setSelectedRecipient(recipient);
    setAmount('');
    setWalletType('cash_wallet');
    setShowModal(true);
  };

  const handleTransfer = async () => {
    const transferAmount = parseFloat(amount);
    if (!transferAmount || transferAmount < 10) {
      toast.error('Minimum transfer amount is ₹10');
      return;
    }

    const walletBalance = walletType === 'cash_wallet' ? balances.cash_wallet : balances.commission_wallet;
    if (walletBalance < transferAmount + 10) { // Include potential charge
      toast.error('Insufficient balance');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/wallet/transfer', {
        recipient_username: selectedRecipient.username,
        amount: transferAmount,
        wallet_type: walletType
      });
      toast.success(`₹${transferAmount} transferred to @${selectedRecipient.username}!`);
      setShowModal(false);
      fetchBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return null;
  }

  if (recipients.length === 0) {
    return null; // Don't show if no recent recipients
  }

  return (
    <>
      <div className="glass-card p-6" data-testid="quick-transfer-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Quick Transfer
          </h3>
          <button 
            onClick={() => navigate('/wallets')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            All Wallets <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">Send to recent recipients</p>
        
        <div className="flex gap-3 overflow-x-auto pb-2">
          {recipients.map((recipient) => (
            <button
              key={recipient.id}
              onClick={() => openQuickTransfer(recipient)}
              className="flex flex-col items-center gap-2 p-3 bg-white/5 hover:bg-white/10 rounded-xl min-w-[80px] transition-all hover:scale-105"
              data-testid={`quick-recipient-${recipient.username}`}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <span className="text-xs font-medium truncate max-w-[70px]">
                {recipient.name.split(' ')[0]}
              </span>
              <span className="text-[10px] text-muted-foreground">
                @{recipient.username}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Transfer Modal */}
      {showModal && selectedRecipient && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="glass-card p-6 max-w-sm w-full" data-testid="quick-transfer-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Quick Transfer
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Recipient Info */}
            <div className="flex items-center gap-3 mb-6 p-4 bg-white/5 rounded-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full flex items-center justify-center">
                <User className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="font-bold text-lg">{selectedRecipient.name}</p>
                <p className="text-sm text-muted-foreground">@{selectedRecipient.username}</p>
              </div>
            </div>

            {/* Wallet Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">From Wallet</label>
              <select
                value={walletType}
                onChange={(e) => setWalletType(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
              >
                <option value="cash_wallet">Cash Wallet (₹{balances.cash_wallet?.toFixed(2)})</option>
                <option value="commission_wallet">Commission Wallet (₹{balances.commission_wallet?.toFixed(2)})</option>
              </select>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Amount (₹)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-2xl font-bold"
                placeholder="0.00"
                min="10"
              />
              <p className="text-xs text-muted-foreground mt-1">Min ₹10 • Transfer charges apply</p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex gap-2 mb-6">
              {[100, 500, 1000, 5000].map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-all"
                >
                  ₹{amt}
                </button>
              ))}
            </div>

            {/* Transfer Button */}
            <button
              onClick={handleTransfer}
              disabled={processing || !amount}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {processing ? (
                'Transferring...'
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Transfer ₹{amount || '0'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickTransfer;
